import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Search } from "lucide-react";
import { documentService, downloadFile } from "@/services/documentService";
import { Document } from "@/types/document";
import { DocumentTable } from "@/components/documents/DocumentTable";
import { DocumentUploadModal } from "@/components/documents/DocumentUploadModal";
import { DocumentEditModal } from "@/components/documents/DocumentEditModal";
import { CreateAssignmentModal } from "@/components/assignments/CreateAssignmentModal";
import { toast } from "@/hooks/use-toast";

const DocumentLibrary = () => {
  const [searchInput, setSearchInput] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );

  const queryClient = useQueryClient();

  // Fetch all documents (no filters)
  const {
    data: documentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["documents"],
    queryFn: () => documentService.getDocuments({ size: 1000 }), // Get all documents
  });

  // Client-side filtering based on search input
  const filteredDocuments =
    documentsData?.content?.filter((document) =>
      document.title.toLowerCase().includes(searchInput.toLowerCase()),
    ) || [];

  const deleteDocumentMutation = useMutation({
    mutationFn: documentService.deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    },
  });

  const downloadDocumentMutation = useMutation({
    mutationFn: documentService.downloadDocument,
    onSuccess: (blob, documentId) => {
      const document = documentsData?.content.find(
        (doc) => doc.id === documentId,
      );
      if (document) {
        downloadFile(blob, document.fileName);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to download document",
        variant: "destructive",
      });
    },
  });

  const handleSearch = (searchTerm: string) => {
    setSearchInput(searchTerm);
  };

  const handleEdit = (document: Document) => {
    setSelectedDocument(document);
    setShowEditModal(true);
  };

  const handleAssign = (document: Document) => {
    setSelectedDocument(document);
    setShowAssignModal(true);
  };

  const handleDelete = async (document: Document) => {
    if (
      window.confirm(`Are you sure you want to delete "${document.title}"?`)
    ) {
      deleteDocumentMutation.mutate(document.id);
    }
  };

  const handleDownload = (document: Document) => {
    downloadDocumentMutation.mutate(document.id);
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              Error loading documents. Please try again.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Document Library
          </h1>
          <p className="text-muted-foreground">
            Manage and organize your documents
          </p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Documents</CardTitle>
          <CardDescription>
            Find documents by searching their names
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search documents..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <DocumentTable
        documents={filteredDocuments}
        totalPages={1}
        currentPage={0}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onAssign={handleAssign}
        onPageChange={() => {}} // No pagination needed
      />

      {/* Modals */}
      {showUploadModal && (
        <DocumentUploadModal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["documents"] });
            setShowUploadModal(false);
          }}
        />
      )}

      {showEditModal && selectedDocument && (
        <DocumentEditModal
          open={showEditModal}
          document={selectedDocument}
          onClose={() => {
            setShowEditModal(false);
            setSelectedDocument(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["documents"] });
            setShowEditModal(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {showAssignModal && selectedDocument && (
        <CreateAssignmentModal
          open={showAssignModal}
          preselectedDocument={selectedDocument}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedDocument(null);
          }}
          onSuccess={() => {
            setShowAssignModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default DocumentLibrary;
